import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { SITE_URL } from "@/lib/seo";

/**
 * Visual + structured breadcrumbs.
 * Accepts an array of { label, to } where `to` is omitted on the last (current) item.
 * Automatically emits a BreadcrumbList JSON-LD.
 */
export default function Breadcrumbs({ items = [], homeLabel = "Inici" }) {
  const trail = [{ label: homeLabel, to: "/" }, ...items];

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: item.label,
      item: item.to ? `${SITE_URL}${item.to}` : undefined,
    })),
  };

  return (
    <nav
      aria-label="breadcrumb"
      data-testid="breadcrumbs"
      className="text-xs text-[#475569]"
    >
      <ol className="flex items-center flex-wrap gap-1.5 font-sans-display tracking-wide">
        {trail.map((item, idx) => {
          const isLast = idx === trail.length - 1;
          return (
            <li key={`${item.label}-${idx}`} className="flex items-center gap-1.5">
              {idx > 0 && <ChevronRight className="h-3 w-3 text-slate-400" />}
              {isLast || !item.to ? (
                <span
                  className="text-[#0B2545] font-medium"
                  aria-current="page"
                  data-testid={`breadcrumb-current`}
                >
                  {idx === 0 ? <Home className="h-3.5 w-3.5" /> : item.label}
                </span>
              ) : (
                <Link
                  to={item.to}
                  className="hover:text-[#0B2545] transition flex items-center gap-1"
                  data-testid={`breadcrumb-link-${idx}`}
                >
                  {idx === 0 ? <Home className="h-3.5 w-3.5" /> : item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </nav>
  );
}
